import React from 'react';
import { JobSearchResult } from '@/types/jobSearch';
import { Job } from '@/components/jobs/types';
import { useMatchingCandidatesLogic } from '@/components/jobs/detail/matching-candidates/hooks/useMatchingCandidatesLogic';
import { MatchingCandidate } from '@/components/candidates/MatchingCandidate';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';

interface JobSearchResultMatchingCandidatesPreviewProps {
  searchResultJob: JobSearchResult;
}

const MAX_PREVIEW_CANDIDATES = 3;

export const JobSearchResultMatchingCandidatesPreview = ({ searchResultJob }: JobSearchResultMatchingCandidatesPreviewProps) => {
  const navigate = useNavigate();

  const parseExperience = (level?: string): number | null => {
    if (!level) return null;
    const match = level.match(/\d+/);
    if (match) return parseInt(match[0], 10);
    return null;
  };

  const jobForMatching: Job = {
    id: searchResultJob.id, 
    title: searchResultJob.title, 
    company: searchResultJob.company_name, 
    location: searchResultJob.location, 
    description: searchResultJob.description || searchResultJob.snippet || '', 
    skills: Array.isArray((searchResultJob as any).skills) ? (searchResultJob as any).skills : (searchResultJob.raw_data?.skills || []),
    experience_required: parseExperience(searchResultJob.experience_level) ?? ((searchResultJob as any).experienceYears as number | undefined) ?? null,
    posted: searchResultJob.posted_date || new Date().toISOString(), 
    status: 'open', 
    type: searchResultJob.employment_type || 'Full-time', 
    user_id: '', 
  };
  
  const {
    isLoading,
    error,
    suggestedCandidates,
    user,
  } = useMatchingCandidatesLogic(jobForMatching);

  if (!user) {
    return null;
  }

  if (!jobForMatching.id || !jobForMatching.title) { 
    console.warn("JobSearchResultMatchingCandidatesPreview: Missing ID or Title for matching logic.", jobForMatching);
    return null;
  }

  const handleViewAllMatches = () => {
    navigate(`/jobs/${jobForMatching.id}`);
  };

  if (isLoading) {
    return (
      <div className="mt-4 p-4 border-t">
        <h4 className="font-semibold mb-2 text-sm">Suggested Candidates</h4>
        {[...Array(MAX_PREVIEW_CANDIDATES)].map((_, i) => (
          <Skeleton key={i} className="h-20 mb-2 rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 p-4 border-t">
        <h4 className="font-semibold mb-2 text-sm text-destructive flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          Error loading suggestions
        </h4>
        <p className="text-xs text-muted-foreground pl-6">{error.message}</p>
      </div>
    );
  }
  
  const topDisplayCandidates = suggestedCandidates ? suggestedCandidates.slice(0, MAX_PREVIEW_CANDIDATES) : [];

  if (topDisplayCandidates.length === 0 && !isLoading) {
    return (
      <div className="mt-4 p-4 border-t">
        <h4 className="font-semibold mb-2 text-sm">Suggested Candidates</h4>
        <p className="text-xs text-muted-foreground">No suggested candidates found for this job.</p>
      </div>
    );
  }
  
  if (topDisplayCandidates.length === 0) {
      return null;
  }

  return (
    <div className="mt-4 p-4 border-t">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold text-sm">
          Top {topDisplayCandidates.length} Suggested Candidate{topDisplayCandidates.length === 1 ? '' : 's'}
        </h4>
        {jobForMatching.id && (
          <Button variant="link" size="sm" onClick={handleViewAllMatches} className="text-xs h-auto p-0">
            View all &rarr;
          </Button>
        )}
      </div>
      <div className="space-y-1">
        {topDisplayCandidates.map(candidateSuggestion => (
          <MatchingCandidate 
            key={candidateSuggestion.id} 
            suggestion={candidateSuggestion} 
            jobId={jobForMatching.id} 
            jobTitle={jobForMatching.title || "Untitled Job"} 
            context="preview" 
          />
        ))}
      </div>
    </div>
  );
};
